<template>
  <CollapsibleSection
    title="Argumentaire commercial"
    icon="fas fa-bullhorn"
    context="quote"
    storage-key="argumentaire-commercial"
    :default-collapsed="true"
    :use-grid="false"
    :count="allArgumentaires.length"
    :bare="bare"
  >
    <LoadingSpinner v-if="loading" position="inline" message="Chargement des argumentaires..." />

    <DetailEmpty v-else-if="hasNoArgumentaires" tag="p">
      Aucun argumentaire disponible. Aucun argumentaire n'est configuré pour cette formation.
    </DetailEmpty>

    <div v-else class="argumentaires-list">
      <div
        v-for="argumentaire in allArgumentaires"
        :key="`${argumentaire.scope}-${argumentaire.id}`"
        class="argumentaire-item"
      >
        <div class="argumentaire-item-header">
          <span class="argumentaire-title">{{ argumentaire.title }}</span>
          <Badge :variant="argumentaire.scope === 'formation' ? 'info' : 'secondary'" size="sm">
            {{ argumentaire.scope === 'formation' ? 'Formation' : 'Général' }}
          </Badge>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -- contenu rédigé au back-office -->
        <div class="list-rich-text" v-html="argumentaire.content"></div>
      </div>
    </div>
  </CollapsibleSection>
</template>

<script setup>
import { computed, toRef } from 'vue'
import { CollapsibleSection, DetailEmpty } from '../data'
import { LoadingSpinner, Badge } from '../ui'
import { useQuoteDetailArgumentaire } from '../../composables/quotes/useQuoteDetailArgumentaire.js'

const props = defineProps({
  /**
   * Mode « nu » : le contenu seul, sans carte ni en-tête repliable. La fenêtre
   * « Analyse du besoin » v2 n'affiche qu'une section à la fois et porte
   * elle-même son titre — la carte y ferait un cadre dans un cadre.
   * Additif : sans la prop, le rendu est exactement celui d'avant.
   */
  bare: {
    type: Boolean,
    default: false
  },
  formationNom: {
    type: String,
    default: null
  }
})

const formationNomRef = toRef(props, 'formationNom')

const {
  loading,
  formationArgumentaires,
  generalArgumentaires,
  hasNoArgumentaires
} = useQuoteDetailArgumentaire(formationNomRef)

// Une seule liste : ceux de la formation demandée d'abord, puis les généraux.
// Le sujet de chaque argumentaire se lit sur sa pastille, plus besoin de deux
// sous-titres qui coupaient la section en deux.
const allArgumentaires = computed(() => [
  ...formationArgumentaires.value.map((a) => ({ ...a, scope: 'formation' })),
  ...generalArgumentaires.value.map((a) => ({ ...a, scope: 'general' }))
])
</script>

<style scoped>
.argumentaires-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

.argumentaire-item {
  padding: var(--spacing-3) var(--spacing-4);
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
}

.argumentaire-item-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-1);
}

.argumentaire-title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}

/* La mise en forme du texte rédigé est la classe partagée `.list-rich-text`
   (src/styles/list-v2.css) : les six règles qui vivaient ici en étaient une
   copie qui avait déjà divergé de celle du catalogue (retrait lg contre 5,
   marge sm contre xs). Un `v-html` n'hérite de rien, chaque écran qui en rend
   doit tout redonner — d'où une seule écriture, pas une par écran. */

@media (max-width: 768px) {
  .argumentaire-item {
    padding: var(--spacing-3);
  }
}
</style>
